<template>
  <div class="max-w-6xl mx-auto">
    <div class="bg-background-light rounded-lg shadow-sm">
      <div class="p-6 border-b border-background-lighter">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 class="text-2xl font-bold text-text-primary">Login History</h2>
            <p class="text-text-secondary mt-1">Monitor your account access and security</p>
          </div>

          <!-- Filters -->
          <div class="mt-4 sm:mt-0 flex space-x-3">
            <input
                v-model="filters.date_from"
                type="date"
                class="px-3 py-2 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary"
                @change="fetchLoginHistory"
            />

            <input
                v-model="filters.date_to"
                type="date"
                class="px-3 py-2 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary"
                @change="fetchLoginHistory"
            />
          </div>
        </div>
      </div>

      <div class="p-6">
        <!-- Loading State -->
        <div v-if="isLoading" class="flex justify-center items-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>

        <!-- Login History Table -->
        <div v-else-if="loginHistory.length > 0" class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-background-lighter">
            <tr>
              <th class="px-4 py-3 text-left text-sm font-medium text-text-secondary">Login Time</th>
              <th class="px-4 py-3 text-left text-sm font-medium text-text-secondary">IP Address</th>
              <th class="px-4 py-3 text-left text-sm font-medium text-text-secondary">Browser</th>
              <th class="px-4 py-3 text-left text-sm font-medium text-text-secondary">Device</th>
              <th class="px-4 py-3 text-left text-sm font-medium text-text-secondary">Operating System</th>
            </tr>
            </thead>
            <tbody class="divide-y divide-background-lighter">
            <tr
                v-for="login in loginHistory"
                :key="login.id"
                class="hover:bg-background-lighter"
            >
              <td class="px-4 py-3 text-sm text-text-primary">
                {{ formatDate(login.login_time) }}
              </td>

              <td class="px-4 py-3 text-sm font-mono text-text-primary">
                {{ login.ip_address }}
                <span v-if="isCurrentSession(login)" class="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                  Current
                </span>
              </td>

              <td class="px-4 py-3 text-sm text-text-primary">
                <div class="flex items-center">
                  <svg v-if="login.browser === 'Chrome'" class="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0zm5.94 7.54l-3.66 6.35a4 4 0 01-6.95-4l3.66-6.35a8 8 0 016.95 4z" fill="#4285f4"/>
                  </svg>
                  <svg v-else-if="login.browser === 'Firefox'" class="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0z" fill="#ff7139"/>
                  </svg>
                  <svg v-else class="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0z" fill="#666"/>
                  </svg>
                  {{ login.browser }}
                </div>
              </td>

              <td class="px-4 py-3 text-sm text-text-primary">
                {{ login.device }}
              </td>

              <td class="px-4 py-3 text-sm text-text-primary">
                <div class="flex items-center">
                  <svg v-if="login.os === 'Windows'" class="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M0 3.4l9.6-1.3v9.3H0m10.7-9.3L24 1.2v10.8H10.7M0 13.6h9.6v9.3L0 21.6m10.7.1L24 22.8V12H10.7" fill="#00adef"/>
                  </svg>
                  <svg v-else-if="login.os === 'Mac'" class="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" fill="#666"/>
                  </svg>
                  <svg v-else class="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0z" fill="#666"/>
                  </svg>
                  {{ login.os }}
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-8">
          <svg class="mx-auto h-12 w-12 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
          </svg>
          <p class="mt-2 text-text-secondary">No login history found</p>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.total > pagination.per_page" class="mt-6 flex justify-center">
          <nav class="flex items-center space-x-2">
            <button
                @click="changePage(pagination.current_page - 1)"
                :disabled="pagination.current_page === 1"
                class="px-3 py-2 text-sm bg-background border border-background-lighter rounded hover:bg-background-lighter disabled:opacity-50"
            >
              Previous
            </button>

            <span class="px-3 py-2 text-sm text-text-secondary">
              Page {{ pagination.current_page }} of {{ pagination.last_page }}
            </span>

            <button
                @click="changePage(pagination.current_page + 1)"
                :disabled="pagination.current_page === pagination.last_page"
                class="px-3 py-2 text-sm bg-background border border-background-lighter rounded hover:bg-background-lighter disabled:opacity-50"
            >
              Next
            </button>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { getUserLoginHistory } from '@/services/api.js';
import Swal from 'sweetalert2';

export default {
  name: 'LoginHistory',
  setup() {
    const loginHistory = ref([]);
    const isLoading = ref(true);

    const filters = ref({
      date_from: '',
      date_to: ''
    });

    const pagination = ref({
      current_page: 1,
      last_page: 1,
      per_page: 10,
      total: 0
    });

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
      });
    };

    const isCurrentSession = (login) => {
      // You can implement logic to check if this is the current session
      // For now, we'll mark the most recent login as current
      return loginHistory.value.length > 0 && loginHistory.value[0].id === login.id;
    };

    const fetchLoginHistory = async (page = 1) => {
      isLoading.value = true;

      try {
        const params = {
          page,
          per_page: pagination.value.per_page,
          date_from: filters.value.date_from,
          date_to: filters.value.date_to
        };

        const response = await getUserLoginHistory(params);

        // Adjust based on your actual response structure
        const data = response.data || response;
        loginHistory.value = data.loginHistory || data || [];
        pagination.value = data.pagination || pagination.value;

      } catch (error) {
        console.error('Failed to fetch login history:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to load login history. Please try again.',
          icon: 'error'
        });
      } finally {
        isLoading.value = false;
      }
    };

    const changePage = (page) => {
      if (page >= 1 && page <= pagination.value.last_page) {
        fetchLoginHistory(page);
      }
    };

    onMounted(() => {
      fetchLoginHistory();
    });

    return {
      loginHistory,
      isLoading,
      filters,
      pagination,
      formatDate,
      isCurrentSession,
      fetchLoginHistory,
      changePage
    };
  }
}
</script>
